import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors, semanticColors } from '@/lib/colors';
import { LockerStatusData } from '@/features/locker/types';
import {
    formatLockNumber,
    getEffectiveLockNumber,
    getStatusBadge,
    isIndividualOverride,
    isOccupiedStatus,
} from '@/features/locker/lockerDisplay';

/**
 * 라커 카드 한 장.
 *
 * ■ 왜 memo 인가
 * 한 페이지에 100장이 뜬다. 예전에는 카드를 선택할 때마다 페이지 전체가 다시 그려져
 * 클릭 반응이 눈에 띄게 느렸다. 카드를 떼고 `isSelected` 만 내려주면 선택이 바뀔 때
 * 두 장(이전·현재)만 다시 그린다.
 *
 * ■ 왜 hover 를 CSS 로 옮겼나
 * 예전에는 카드마다 onMouseEnter/onMouseLeave 로 style.borderColor 를 직접 만졌다.
 * 100장이면 인라인 핸들러 200개 + hover 마다 강제 스타일 재계산이다. 색은 CSS 변수로
 * 내려주고 상태 전이는 CSS 가 하게 했다 — 자바스크립트 비용이 0 이 된다.
 */

/** 카드가 쓰는 CSS. 카드마다 넣으면 100개가 되므로 목록 위에서 **한 번만** 그린다. */
export const LockerCardStyles = () => (
    <style>{`
.locker-card{
  border:1px solid var(--locker-border);
  background:var(--locker-bg);
  transition:border-color .15s ease, box-shadow .15s ease;
}
.locker-card[data-empty="true"]{ border-style:dashed; }
.locker-card:hover{ border-color:var(--locker-border-hover); }
.locker-card:focus-visible{ outline:2px solid ${colors.PRIMARY_BLUE}; outline-offset:2px; }
.locker-card[data-selected="true"]{
  border-color:${colors.PRIMARY_BLUE};
  border-style:solid;
  box-shadow:inset 0 0 0 1px ${colors.PRIMARY_BLUE};
}
.locker-menu-item{ background:transparent; transition:background-color .12s ease; }
.locker-menu-item:hover{ background:var(--locker-menu-hover); }
.locker-menu-item:focus-visible{ outline:2px solid ${colors.PRIMARY_BLUE}; outline-offset:-2px; }
.locker-bulk-input:focus-visible{ outline:2px solid ${colors.PRIMARY_BLUE}; outline-offset:1px; }
`}</style>
);

const menuItemStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
};

type MenuAction = {
    key: string;
    label: string;
    danger?: boolean;
    onSelect: () => void;
};

/**
 * 카드의 액션 메뉴.
 *
 * 예전에는 클릭 가능한 `div` 였다 — 키보드로는 열 수도 고를 수도 없었다.
 * 실제 `<button>` 으로 바꾸고, 열리면 첫 항목에 포커스를 주고, ESC 로 닫으면서
 * 트리거(카드)로 포커스를 되돌린다.
 */
const LockerCardMenu = ({
    title,
    subtitle,
    actions,
    onClose,
}: {
    title?: string;
    subtitle?: string;
    actions: MenuAction[];
    onClose: (refocus: boolean) => void;
}) => {
    const firstItemRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        firstItemRef.current?.focus();
    }, []);

    return (
        <div
            role="menu"
            aria-label="라커 동작"
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    onClose(true);
                }
            }}
            style={{
                position: 'absolute',
                top: 108,
                left: 0,
                minWidth: 188,
                backgroundColor: colors.white,
                borderRadius: 8,
                border: `1px solid ${colors.WHITE_600}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                zIndex: 20,
                overflow: 'hidden',
            }}
        >
            {(title || subtitle) && (
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.WHITE_600}`, backgroundColor: colors.GREY }}>
                    {title && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: colors.LAVEL_4, wordBreak: 'break-all' }}>{title}</div>
                    )}
                    {subtitle && <div style={{ fontSize: 11, color: colors.LAVEL_1, marginTop: 2 }}>{subtitle}</div>}
                </div>
            )}
            {actions.map((action, index) => (
                <React.Fragment key={action.key}>
                    {index > 0 && <div style={{ height: 1, backgroundColor: colors.WHITE_600 }} />}
                    <button
                        type="button"
                        role="menuitem"
                        ref={index === 0 ? firstItemRef : undefined}
                        className="locker-menu-item"
                        onClick={() => action.onSelect()}
                        style={{
                            ...menuItemStyle,
                            color: action.danger ? colors.ERROR : colors.LAVEL_4,
                            ['--locker-menu-hover' as any]: action.danger ? colors.ERROR_50 : colors.GREY,
                        }}
                    >
                        {action.label}
                    </button>
                </React.Fragment>
            ))}
        </div>
    );
};

export type LockerCardProps = {
    locker: LockerStatusData;
    isSelected: boolean;
    revealAll: boolean;
    readOnly: boolean;
    calculateDday: (endDate: string) => number;
    onToggleSelect: (lockerNumber: number) => void;
    onCloseMenu: () => void;
    onAssign: (lockerNumber: number) => void;
    onEditPassword: (lockerNumber: number) => void;
    onRelease: (locker: LockerStatusData) => void;
    onOpenMember: (memberId: string) => void;
};

const LockerCardBase = ({
    locker,
    isSelected,
    revealAll,
    readOnly,
    calculateDday,
    onToggleSelect,
    onCloseMenu,
    onAssign,
    onEditPassword,
    onRelease,
    onOpenMember,
}: LockerCardProps) => {
    const cardRef = useRef<HTMLDivElement | null>(null);
    const [copied, setCopied] = useState(false);

    const occupied = isOccupiedStatus(locker.status);
    const isExpired = locker.status === 'EXPIRED';
    const isPaused = locker.status === 'PAUSED';
    const sub = locker.subscription;
    const badge = getStatusBadge(locker, calculateDday);
    const effective = getEffectiveLockNumber(locker);
    const showsOwnChip = isIndividualOverride(locker);

    const closeAndRefocus = useCallback(
        (refocus: boolean) => {
            onCloseMenu();
            if (refocus) requestAnimationFrame(() => cardRef.current?.focus());
        },
        [onCloseMenu],
    );

    const copyLockNumber = useCallback(async () => {
        if (!effective) return;
        try {
            await navigator.clipboard.writeText(effective);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // 클립보드 권한이 없거나 http 환경이면 실패한다. 값은 메뉴에 이미 평문으로 보이므로
            // 여기서 실패해도 관리자가 할 일이 막히지 않는다.
            setCopied(false);
        }
    }, [effective]);

    const actions: MenuAction[] = [];
    if (occupied) {
        if (sub?.memberId) {
            actions.push({
                key: 'member',
                label: '회원 상세 보기',
                onSelect: () => onOpenMember(sub.memberId),
            });
        }
        if (!readOnly) {
            actions.push({
                key: 'password',
                label: locker.defaultLockNumber ? '이 칸 고정 비밀번호 변경' : '이 칸 고정 비밀번호 설정',
                onSelect: () => {
                    closeAndRefocus(false);
                    onEditPassword(locker.number);
                },
            });
            actions.push({
                key: 'release',
                label: '라커 회수하기',
                danger: true,
                onSelect: () => onRelease(locker),
            });
        }
    } else if (!readOnly) {
        // 빈 칸은 2지선다. 예전에는 클릭하면 곧바로 배정 모달이라 비밀번호만 미리 넣어 둘 방법이 없었다.
        actions.push({
            key: 'assign',
            label: '회원 배정하기',
            onSelect: () => {
                closeAndRefocus(false);
                onAssign(locker.number);
            },
        });
        actions.push({
            key: 'password',
            label: locker.defaultLockNumber ? '비밀번호 변경' : '비밀번호 설정',
            onSelect: () => {
                closeAndRefocus(false);
                onEditPassword(locker.number);
            },
        });
    }

    // 메뉴 안에서는 평문으로 보여준다. 한 칸을 확인하려고 화면 전체를 공개하지 않게 하는 것이
    // 카드별 공개의 요점이다.
    const menuTitle = effective ? `🔓 ${effective}` : undefined;
    const ownValue = sub?.ownLockNumber || null;
    const fixedValue = locker.defaultLockNumber || null;
    const menuSubtitleParts: string[] = [];
    if (ownValue) menuSubtitleParts.push(`개별 ${ownValue}`);
    if (fixedValue) menuSubtitleParts.push(`이 칸 고정 ${fixedValue}`);
    const menuSubtitle = menuSubtitleParts.length > 0 ? menuSubtitleParts.join(' · ') : undefined;

    const borderColor = isSelected
        ? colors.PRIMARY_BLUE
        : isPaused
          ? semanticColors.warning.border
          : isExpired
            ? semanticColors.danger.border
            : occupied
              ? colors.WHITE_600
              : colors.GREY_100;
    const backgroundColor = isExpired
        ? semanticColors.danger.bg
        : isPaused
          ? semanticColors.warning.bg
          : occupied
            ? colors.white
            : colors.GREY;

    const statusLabel = isPaused ? '정지' : isExpired ? '만료' : occupied ? '배정' : '비어 있음';
    const ariaLabel = `${locker.number}번 라커, ${statusLabel}${occupied && sub?.memberName ? `, ${sub.memberName}` : ''}`;

    /*
     * 고를 것이 하나도 없으면 버튼이 아니다.
     * 열람 전용 계정의 빈 칸이 그렇다 — 그대로 두면 아무 동작도 없는 포커스 대상이
     * 최대 500개 생겨 키보드 사용자가 그리드를 통과할 수 없다.
     */
    const isInteractive = actions.length > 0;

    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={cardRef}
                // 모달을 닫은 뒤 이 카드로 포커스를 되돌릴 때 쓴다(LockerAssignmentTab).
                id={`locker-card-${locker.number}`}
                className="locker-card"
                role={isInteractive ? 'button' : undefined}
                tabIndex={isInteractive ? 0 : undefined}
                aria-label={isInteractive ? ariaLabel : undefined}
                aria-expanded={isInteractive ? isSelected : undefined}
                data-selected={isSelected ? 'true' : 'false'}
                data-empty={occupied ? 'false' : 'true'}
                onClick={isInteractive ? () => onToggleSelect(locker.number) : undefined}
                onKeyDown={(event) => {
                    if (!isInteractive) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onToggleSelect(locker.number);
                    }
                    if (event.key === 'Escape' && isSelected) {
                        event.preventDefault();
                        onCloseMenu();
                    }
                }}
                style={{
                    height: 100,
                    borderRadius: 10,
                    padding: '12px 14px',
                    cursor: isInteractive ? 'pointer' : 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    ['--locker-border' as any]: borderColor,
                    ['--locker-border-hover' as any]: isSelected ? colors.PRIMARY_BLUE : colors.BLUE_200,
                    ['--locker-bg' as any]: backgroundColor,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: occupied ? colors.LAVEL_4 : colors.GREY_200 }}>
                        {locker.number}
                    </span>
                    {badge && (
                        <span
                            style={{
                                padding: '2px 6px',
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor: semanticColors[badge.tone].bg,
                                color: semanticColors[badge.tone].text,
                                border: `1px solid ${semanticColors[badge.tone].border}`,
                                lineHeight: '16px',
                                flex: 'none',
                            }}
                        >
                            {badge.text}
                        </span>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: occupied ? colors.LAVEL_4 : colors.GREY_200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: 2,
                        }}
                    >
                        {occupied ? sub?.memberName || '이름 없음' : '비어 있음'}
                    </span>
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            color: effective ? colors.LAVEL_1 : colors.GREY_200,
                            minWidth: 0,
                        }}
                    >
                        <span aria-hidden="true">🔒</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {formatLockNumber(effective, revealAll)}
                        </span>
                        {/* 개별값이 있으면 붙인다 — 값이 같아도 이후 고정값 변경을 상속하지 않는다. */}
                        {showsOwnChip && (
                            <span
                                style={{
                                    flex: 'none',
                                    padding: '0 4px',
                                    borderRadius: 3,
                                    fontSize: 9,
                                    fontWeight: 800,
                                    lineHeight: '14px',
                                    backgroundColor: semanticColors.info.bg,
                                    color: semanticColors.info.text,
                                    border: `1px solid ${semanticColors.info.border}`,
                                }}
                            >
                                개별
                            </span>
                        )}
                        {/*
                         * 빈 칸인데 비밀번호가 준비된 경우. 색이나 테두리로만 구분하면 색각 이상에서
                         * 읽히지 않으므로 글자로 밝힌다.
                         */}
                        {!occupied && effective && (
                            <span style={{ flex: 'none', fontSize: 9, color: colors.GREY_300 }}>칸 고정</span>
                        )}
                    </span>
                </div>
            </div>

            {isSelected && actions.length > 0 && (
                <LockerCardMenu
                    title={menuTitle}
                    subtitle={menuSubtitle}
                    actions={
                        effective
                            ? [
                                  {
                                      key: 'copy',
                                      label: copied ? '복사했습니다' : '비밀번호 복사',
                                      onSelect: copyLockNumber,
                                  },
                                  ...actions,
                              ]
                            : actions
                    }
                    onClose={closeAndRefocus}
                />
            )}
        </div>
    );
};

export const LockerCard = React.memo(LockerCardBase);

export type BulkCellState = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

const BULK_STATE_STYLE: Record<BulkCellState, { border: string; label: string; color: string }> = {
    idle: { border: colors.WHITE_600, label: '', color: colors.LAVEL_1 },
    dirty: { border: semanticColors.info.border, label: '변경됨', color: semanticColors.info.text },
    saving: { border: semanticColors.info.border, label: '저장 중', color: semanticColors.info.text },
    saved: { border: semanticColors.success.border, label: '저장됨', color: semanticColors.success.text },
    failed: { border: semanticColors.danger.border, label: '실패 · 다시 저장', color: semanticColors.danger.text },
};

export type LockerBulkCellProps = {
    locker: LockerStatusData;
    value: string;
    state: BulkCellState;
    /** 실패 사유. 영구 실패(길이 초과 등)를 이유도 모르고 재시도하지 않게 한다. */
    errorMessage?: string;
    revealAll: boolean;
    isFocused: boolean;
    /**
     * roving tabindex. 100개 입력을 전부 탭 순서에 두면 그리드를 빠져나가는 데만
     * 탭을 수백 번 눌러야 한다. 활성 셀 하나만 0 이고 나머지는 -1 이다.
     */
    tabIndex: number;
    onChange: (lockerNumber: number, value: string) => void;
    onCommit: (lockerNumber: number) => void;
    onFocus: (lockerNumber: number) => void;
    onBlur: (lockerNumber: number) => void;
    onMove: (fromNumber: number, delta: number) => void;
};

const LockerBulkCellBase = ({
    locker,
    value,
    state,
    errorMessage,
    revealAll,
    isFocused,
    tabIndex,
    onChange,
    onCommit,
    onFocus,
    onBlur,
    onMove,
}: LockerBulkCellProps) => {
    const occupied = isOccupiedStatus(locker.status);
    const showsOwnChip = isIndividualOverride(locker);
    const stateStyle = BULK_STATE_STYLE[state];

    /*
     * 모드를 켰다고 전부 공개하지 않는다. **포커스된 셀만** 평문이다.
     *
     * ⚠️ 마스크를 controlled `value` 로 쓰면 안 된다. 포커스 순간 DOM value 가 '••••' 에서
     * 실제 값으로 바뀌면서 **캐럿이 문자열 끝으로 튄다** — 클릭한 위치가 무시되고 중간 글자를
     * 고칠 수 없다(좌우 방향키를 커서에 양보한 의도가 통째로 무력화된다).
     * 그래서 value 는 **항상 실제 값**으로 두고, 가릴 때만 위에 오버레이를 얹는다.
     * 오버레이는 pointer-events:none 이라 클릭이 입력으로 통과하고, 그 클릭으로 포커스가
     * 들어오면 오버레이가 사라져 캐럿이 누른 자리에 그대로 선다.
     */
    const revealed = isFocused || revealAll;
    const isMasked = !revealed && value !== '';

    return (
        <div
            style={{
                height: 100,
                borderRadius: 10,
                padding: '10px 12px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxSizing: 'border-box',
                border: `1px solid ${stateStyle.border}`,
                backgroundColor: colors.white,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: colors.LAVEL_4 }}>{locker.number}</span>
                {showsOwnChip && (
                    <span
                        title="이 회원은 개별 비밀번호를 쓰고 있어 고정값이 적용되지 않습니다."
                        style={{
                            flex: 'none',
                            padding: '0 4px',
                            borderRadius: 3,
                            fontSize: 9,
                            fontWeight: 800,
                            lineHeight: '14px',
                            backgroundColor: semanticColors.info.bg,
                            color: semanticColors.info.text,
                            border: `1px solid ${semanticColors.info.border}`,
                        }}
                    >
                        개별
                    </span>
                )}
            </div>

            <div style={{ position: 'relative' }}>
            <input
                // 방향키·Enter 이동이 이 id 로 다음 셀을 찾는다(LockerAssignmentTab.handleBulkMove).
                id={`locker-bulk-${locker.number}`}
                className="locker-bulk-input"
                // 선행 0(0007)이 보존돼야 한다. number 로 두면 7 이 된다.
                type="text"
                inputMode="numeric"
                autoComplete="off"
                aria-label={`${locker.number}번 라커 고정 비밀번호`}
                placeholder="비밀번호"
                value={value}
                tabIndex={tabIndex}
                onFocus={() => onFocus(locker.number)}
                onBlur={() => onBlur(locker.number)}
                onChange={(event) => onChange(locker.number, event.target.value)}
                onKeyDown={(event) => {
                    /*
                     * 좌우는 **입력 커서에 양보한다.** 셀 이동에 써 버리면 비밀번호 중간 글자를
                     * 고칠 수 없다("0007" 의 두 번째 0 을 지우려면 커서를 옮겨야 한다).
                     * 셀 이동은 Enter(확정 후 다음)와 상하 방향키가 맡는다.
                     */
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        onCommit(locker.number);
                        onMove(locker.number, 1);
                        return;
                    }
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        onMove(locker.number, 1);
                        return;
                    }
                    if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        onMove(locker.number, -1);
                    }
                }}
                style={{
                    width: '100%',
                    padding: '7px 10px',
                    border: `1px solid ${colors.WHITE_600}`,
                    borderRadius: 8,
                    fontSize: 13,
                    color: isMasked ? 'transparent' : colors.LAVEL_4,
                    outline: 'none',
                    boxSizing: 'border-box',
                }}
            />
            {isMasked && (
                <span
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        left: 11,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 13,
                        letterSpacing: 1,
                        color: colors.LAVEL_4,
                        pointerEvents: 'none',
                    }}
                >
                    ••••
                </span>
            )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4, minHeight: 14 }}>
                <span style={{ fontSize: 10, color: occupied ? colors.LAVEL_1 : colors.GREY_200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {occupied ? locker.subscription?.memberName || '이름 없음' : '비어 있음'}
                </span>
                {stateStyle.label && (
                    <span
                        // 실패 사유를 툴팁으로도 준다. 셀이 좁아 한 줄로는 다 못 보여준다.
                        title={state === 'failed' ? errorMessage : undefined}
                        style={{ flex: 'none', fontSize: 10, fontWeight: 700, color: stateStyle.color }}
                    >
                        {stateStyle.label}
                    </span>
                )}
            </div>
            {state === 'failed' && errorMessage && (
                <div
                    role="alert"
                    style={{
                        fontSize: 10,
                        lineHeight: '13px',
                        color: semanticColors.danger.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export const LockerBulkCell = React.memo(LockerBulkCellBase);
